/**
 * Движок формул интерактивной таблицы (fkTable) — Excel-подобный.
 * Чистый модуль без React/DOM — поэтому покрыт unit-тестами (fk-formula.test.ts).
 * Используется в frontend-wiki/src/editor/fk-table.tsx.
 */

export interface FxColLike { id: string }
export interface FxRowLike { id: string; cells: Record<string, string> }

//
// Ячейка, начинающаяся с «=», трактуется как формула. Поддержаны ссылки на
// ячейки (A1), диапазоны (A1:B5), операторы + - * / ^ и сравнения, а также
// топ-20 функций (SUM/СУММ, AVERAGE/СРЗНАЧ, IF/ЕСЛИ, …). Парсер — рекурсивный
// спуск (без eval), есть защита от циклических ссылок (#ЦИКЛ).

/** Топ-функции для подсказки при вводе «=». */
export const FORMULA_FUNCS: { name: string; tpl: string; desc: string }[] = [
  { name: 'SUM', tpl: 'SUM(A1:A5)', desc: 'Сумма диапазона' },
  { name: 'AVERAGE', tpl: 'AVERAGE(A1:A5)', desc: 'Среднее значение' },
  { name: 'MIN', tpl: 'MIN(A1:A5)', desc: 'Минимум' },
  { name: 'MAX', tpl: 'MAX(A1:A5)', desc: 'Максимум' },
  { name: 'COUNT', tpl: 'COUNT(A1:A5)', desc: 'Кол-во чисел' },
  { name: 'COUNTA', tpl: 'COUNTA(A1:A5)', desc: 'Кол-во непустых' },
  { name: 'PRODUCT', tpl: 'PRODUCT(A1:A5)', desc: 'Произведение' },
  { name: 'ROUND', tpl: 'ROUND(A1; 2)', desc: 'Округление' },
  { name: 'ROUNDUP', tpl: 'ROUNDUP(A1; 2)', desc: 'Округление вверх' },
  { name: 'ROUNDDOWN', tpl: 'ROUNDDOWN(A1; 2)', desc: 'Округление вниз' },
  { name: 'ABS', tpl: 'ABS(A1)', desc: 'Модуль числа' },
  { name: 'SQRT', tpl: 'SQRT(A1)', desc: 'Квадратный корень' },
  { name: 'POWER', tpl: 'POWER(A1; 2)', desc: 'Возведение в степень' },
  { name: 'MOD', tpl: 'MOD(A1; B1)', desc: 'Остаток от деления' },
  { name: 'INT', tpl: 'INT(A1)', desc: 'Целая часть' },
  { name: 'IF', tpl: 'IF(A1>0; "да"; "нет")', desc: 'Условие ЕСЛИ' },
  { name: 'AND', tpl: 'AND(A1>0; B1>0)', desc: 'Логическое И' },
  { name: 'OR', tpl: 'OR(A1>0; B1>0)', desc: 'Логическое ИЛИ' },
  { name: 'CONCAT', tpl: 'CONCAT(A1; B1)', desc: 'Объединить текст' },
  { name: 'MEDIAN', tpl: 'MEDIAN(A1:A5)', desc: 'Медиана' },
  { name: 'COUNTIF', tpl: 'COUNTIF(A1:A5; ">0")', desc: 'Кол-во по условию' },
];

/** Русские псевдонимы и сокращения → каноническое имя функции. */
const FX_ALIAS: Record<string, string> = {
  СУММ: 'SUM', СРЗНАЧ: 'AVERAGE', AVG: 'AVERAGE', МИН: 'MIN', МАКС: 'MAX',
  СЧЁТ: 'COUNT', СЧЕТ: 'COUNT', СЧЁТЗ: 'COUNTA', СЧЕТЗ: 'COUNTA',
  ПРОИЗВЕД: 'PRODUCT', ОКРУГЛ: 'ROUND', ОКРУГЛВВЕРХ: 'ROUNDUP', ОКРУГЛВНИЗ: 'ROUNDDOWN',
  КОРЕНЬ: 'SQRT', СТЕПЕНЬ: 'POWER', ОСТАТ: 'MOD', ЦЕЛОЕ: 'INT',
  ЕСЛИ: 'IF', И: 'AND', ИЛИ: 'OR', НЕ: 'NOT',
  СЦЕПИТЬ: 'CONCAT', CONCATENATE: 'CONCAT', МЕДИАНА: 'MEDIAN',
  СЧЁТЕСЛИ: 'COUNTIF', СЧЕТЕСЛИ: 'COUNTIF',
};

export type FxVal = number | string;

export function colIndexToLetters(i: number): string {
  if (i < 0) return '?';
  let s = '';
  let n = i + 1;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}
function lettersToColIndex(s: string): number {
  let n = 0;
  for (const ch of s.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}
function isNumericStr(s: string): boolean {
  return /^-?\d+(?:[.,]\d+)?$/.test(s.trim());
}
function fxNum(v: FxVal): number {
  if (typeof v === 'number') return v;
  const t = (v ?? '').toString().trim();
  if (t === '') return 0;
  if (isNumericStr(t)) return parseFloat(t.replace(',', '.'));
  throw { code: '#ЗНАЧ' };
}
function fxTruthy(v: FxVal): boolean {
  if (typeof v === 'number') return v !== 0;
  return (v ?? '').toString().trim() !== '';
}
function fxFormat(v: FxVal): string {
  if (typeof v === 'number') {
    if (!isFinite(v)) return '#ЧИСЛО';
    return String(Math.round(v * 1e10) / 1e10);
  }
  return (v ?? '').toString();
}

// — Токенайзер —
function fxTokenize(src: string): { k: string; v?: string | number }[] {
  const t: { k: string; v?: string | number }[] = [];
  const isDigit = (c: string) => c >= '0' && c <= '9';
  const isAlpha = (c: string) => /[A-Za-zА-Яа-яёЁ_]/.test(c);
  let i = 0;
  while (i < src.length) {
    const c = src[i];
    if (c === ' ' || c === '\t') { i++; continue; }
    if (isDigit(c) || (c === '.' && isDigit(src[i + 1]))) {
      let j = i + 1;
      while (j < src.length && (isDigit(src[j]) || src[j] === '.')) j++;
      t.push({ k: 'num', v: parseFloat(src.slice(i, j)) });
      i = j; continue;
    }
    if (c === '"') {
      let j = i + 1; let s = '';
      while (j < src.length && src[j] !== '"') { s += src[j]; j++; }
      t.push({ k: 'str', v: s }); i = j + 1; continue;
    }
    if (isAlpha(c)) {
      let j = i + 1;
      while (j < src.length && (isAlpha(src[j]) || isDigit(src[j]))) j++;
      t.push({ k: 'id', v: src.slice(i, j) }); i = j; continue;
    }
    const two = src.slice(i, i + 2);
    if (two === '<=' || two === '>=' || two === '<>') { t.push({ k: 'op', v: two }); i += 2; continue; }
    if (c === '+' || c === '-' || c === '*' || c === '/' || c === '^') { t.push({ k: 'op', v: c }); i++; continue; }
    if (c === '<' || c === '>' || c === '=') { t.push({ k: 'op', v: c }); i++; continue; }
    if (c === '(') { t.push({ k: 'lp' }); i++; continue; }
    if (c === ')') { t.push({ k: 'rp' }); i++; continue; }
    if (c === ',' || c === ';') { t.push({ k: 'sep' }); i++; continue; }
    if (c === ':') { t.push({ k: 'colon' }); i++; continue; }
    throw { code: '#ОШ' };
  }
  return t;
}

// — Парсер (рекурсивный спуск) → AST —
/* eslint-disable @typescript-eslint/no-explicit-any */
function fxParse(toks: any[]): any {
  let p = 0;
  const peek = () => toks[p];
  const next = () => toks[p++];
  const isOp = (...ops: string[]) => peek() && peek().k === 'op' && ops.includes(peek().v);

  function parseExpr(): any { return parseCompare(); }
  function parseCompare(): any {
    let left = parseAdd();
    while (isOp('<', '>', '<=', '>=', '<>', '=')) { const op = next().v; left = { t: 'bin', op, left, right: parseAdd() }; }
    return left;
  }
  function parseAdd(): any {
    let left = parseMul();
    while (isOp('+', '-')) { const op = next().v; left = { t: 'bin', op, left, right: parseMul() }; }
    return left;
  }
  function parseMul(): any {
    let left = parseUnary();
    while (isOp('*', '/')) { const op = next().v; left = { t: 'bin', op, left, right: parseUnary() }; }
    return left;
  }
  function parseUnary(): any {
    if (isOp('-', '+')) { const op = next().v; return { t: 'unary', op, arg: parseUnary() }; }
    return parsePow();
  }
  function parsePow(): any {
    const left = parsePrimary();
    if (isOp('^')) { next(); return { t: 'bin', op: '^', left, right: parseUnary() }; }
    return left;
  }
  function parsePrimary(): any {
    const tk = peek();
    if (!tk) throw { code: '#ОШ' };
    if (tk.k === 'num') { next(); return { t: 'num', v: tk.v }; }
    if (tk.k === 'str') { next(); return { t: 'str', v: tk.v }; }
    if (tk.k === 'lp') { next(); const e = parseExpr(); if (!peek() || peek().k !== 'rp') throw { code: '#ОШ' }; next(); return e; }
    if (tk.k === 'id') {
      next();
      if (peek() && peek().k === 'lp') {
        next();
        const args: any[] = [];
        if (peek() && peek().k !== 'rp') {
          args.push(parseExpr());
          while (peek() && peek().k === 'sep') { next(); args.push(parseExpr()); }
        }
        if (!peek() || peek().k !== 'rp') throw { code: '#ОШ' };
        next();
        return { t: 'call', name: String(tk.v).toUpperCase(), args };
      }
      if (peek() && peek().k === 'colon') {
        next();
        const tk2 = peek();
        if (!tk2 || tk2.k !== 'id') throw { code: '#ОШ' };
        next();
        return { t: 'range', from: tk.v, to: tk2.v };
      }
      return { t: 'ref', ref: tk.v };
    }
    throw { code: '#ОШ' };
  }
  const e = parseExpr();
  if (p < toks.length) throw { code: '#ОШ' };
  return e;
}

/**
 * Строит движок формул над текущими columns/rows. Возвращает функцию
 * display(rowId, colId) → { text, error } для отображения вычисленной ячейки.
 */
export function makeEngine(columns: FxColLike[], rows: FxRowLike[]): (rowId: string, colId: string) => { text: string; error: boolean } {
  const colIdx = new Map<string, number>(); columns.forEach((c, i) => colIdx.set(c.id, i));
  const rowIdx = new Map<string, number>(); rows.forEach((r, i) => rowIdx.set(r.id, i));
  const memo = new Map<string, FxVal>();
  const visiting = new Set<string>();

  const parseRef = (ref: string): { c: number; r: number } | null => {
    const m = /^([A-Za-z]+)(\d+)$/.exec(ref);
    if (!m) return null;
    return { c: lettersToColIndex(m[1]), r: parseInt(m[2], 10) - 1 };
  };

  function resolveCell(c: number, r: number): FxVal {
    if (c < 0 || r < 0) throw { code: '#ССЫЛ' };
    const col = columns[c]; const row = rows[r];
    if (!col || !row) throw { code: '#ССЫЛ' };
    const key = c + ',' + r;
    if (memo.has(key)) return memo.get(key)!;
    if (visiting.has(key)) throw { code: '#ЦИКЛ' };
    const raw = (row.cells[col.id] ?? '').toString();
    let val: FxVal;
    if (raw.startsWith('=')) {
      visiting.add(key);
      try {
        const v = evalNode(fxParse(fxTokenize(raw.slice(1))));
        if (v && (v as any).__range) throw { code: '#ЗНАЧ' };
        val = v as FxVal;
      } finally {
        visiting.delete(key);
      }
    } else {
      val = raw === '' ? '' : (isNumericStr(raw) ? parseFloat(raw.replace(',', '.')) : raw);
    }
    memo.set(key, val);
    return val;
  }

  function rangeVals(from: string, to: string): FxVal[] {
    const a = parseRef(from); const b = parseRef(to);
    if (!a || !b) throw { code: '#ССЫЛ' };
    const c1 = Math.min(a.c, b.c); const c2 = Math.max(a.c, b.c);
    const r1 = Math.min(a.r, b.r); const r2 = Math.max(a.r, b.r);
    const out: FxVal[] = [];
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) out.push(resolveCell(c, r));
    return out;
  }

  const argNums = (args: any[]): number[] => {
    const out: number[] = [];
    for (const a of args) {
      const v = evalNode(a);
      const arr: any[] = v && (v as any).__range ? (v as any).__range : [v];
      for (const x of arr) {
        if (typeof x === 'number') out.push(x);
        else if (typeof x === 'string' && isNumericStr(x)) out.push(parseFloat(x.replace(',', '.')));
      }
    }
    return out;
  };
  const argAll = (args: any[]): FxVal[] => {
    const out: FxVal[] = [];
    for (const a of args) {
      const v = evalNode(a);
      const arr: any[] = v && (v as any).__range ? (v as any).__range : [v];
      for (const x of arr) out.push(x);
    }
    return out;
  };

  const matchCrit = (x: FxVal, crit: string): boolean => {
    const m = /^(<=|>=|<>|=|<|>)?\s*([\s\S]*)$/.exec(crit.trim());
    const op = (m && m[1]) || '=';
    const rhs = (m && m[2]) || '';
    if (isNumericStr(rhs) && (typeof x === 'number' || (typeof x === 'string' && isNumericStr(x)))) {
      const xn = typeof x === 'number' ? x : parseFloat(x.replace(',', '.'));
      const rn = parseFloat(rhs.replace(',', '.'));
      switch (op) {
        case '=': return xn === rn;
        case '<>': return xn !== rn;
        case '<': return xn < rn;
        case '>': return xn > rn;
        case '<=': return xn <= rn;
        case '>=': return xn >= rn;
      }
    }
    const xs = (x ?? '').toString();
    if (op === '=') return xs === rhs;
    if (op === '<>') return xs !== rhs;
    return false;
  };

  function evalBin(n: any): FxVal {
    const op = n.op as string;
    if (op === '=' || op === '<>' || op === '<' || op === '>' || op === '<=' || op === '>=') {
      const a = evalNode(n.left); const b = evalNode(n.right);
      let cmp: number;
      if (typeof a === 'number' && typeof b === 'number') cmp = a === b ? 0 : a < b ? -1 : 1;
      else cmp = (a ?? '').toString().localeCompare((b ?? '').toString(), 'ru');
      let res = false;
      switch (op) {
        case '=': res = cmp === 0; break;
        case '<>': res = cmp !== 0; break;
        case '<': res = cmp < 0; break;
        case '>': res = cmp > 0; break;
        case '<=': res = cmp <= 0; break;
        case '>=': res = cmp >= 0; break;
      }
      return res ? 1 : 0;
    }
    const a = fxNum(evalNode(n.left)); const b = fxNum(evalNode(n.right));
    switch (op) {
      case '+': return a + b;
      case '-': return a - b;
      case '*': return a * b;
      case '/': if (b === 0) throw { code: '#ДЕЛ/0' }; return a / b;
      case '^': return Math.pow(a, b);
    }
    throw { code: '#ОШ' };
  }

  function evalCall(rawName: string, args: any[]): FxVal {
    const name = FX_ALIAS[rawName] || rawName;
    switch (name) {
      case 'SUM': return argNums(args).reduce((a, b) => a + b, 0);
      case 'AVERAGE': { const n = argNums(args); if (!n.length) throw { code: '#ДЕЛ/0' }; return n.reduce((a, b) => a + b, 0) / n.length; }
      case 'MIN': { const n = argNums(args); return n.length ? Math.min(...n) : 0; }
      case 'MAX': { const n = argNums(args); return n.length ? Math.max(...n) : 0; }
      case 'COUNT': return argNums(args).length;
      case 'COUNTA': return argAll(args).filter((x) => (x ?? '').toString() !== '').length;
      case 'PRODUCT': return argNums(args).reduce((a, b) => a * b, 1);
      case 'ROUND': { const x = fxNum(evalNode(args[0])); const d = args[1] ? fxNum(evalNode(args[1])) : 0; const f = Math.pow(10, d); return Math.round(x * f) / f; }
      case 'ROUNDUP': { const x = fxNum(evalNode(args[0])); const d = args[1] ? fxNum(evalNode(args[1])) : 0; const f = Math.pow(10, d); return (x < 0 ? -1 : 1) * Math.ceil(Math.abs(x) * f) / f; }
      case 'ROUNDDOWN': { const x = fxNum(evalNode(args[0])); const d = args[1] ? fxNum(evalNode(args[1])) : 0; const f = Math.pow(10, d); return (x < 0 ? -1 : 1) * Math.floor(Math.abs(x) * f) / f; }
      case 'ABS': return Math.abs(fxNum(evalNode(args[0])));
      case 'SQRT': { const x = fxNum(evalNode(args[0])); if (x < 0) throw { code: '#ЧИСЛО' }; return Math.sqrt(x); }
      case 'POWER': return Math.pow(fxNum(evalNode(args[0])), fxNum(evalNode(args[1])));
      case 'MOD': { const b = fxNum(evalNode(args[1])); if (b === 0) throw { code: '#ДЕЛ/0' }; return fxNum(evalNode(args[0])) % b; }
      case 'INT': return Math.floor(fxNum(evalNode(args[0])));
      case 'IF': { const c = fxTruthy(evalNode(args[0])); return c ? evalNode(args[1]) : (args[2] !== undefined ? evalNode(args[2]) : 0); }
      case 'AND': return args.every((a) => fxTruthy(evalNode(a))) ? 1 : 0;
      case 'OR': return args.some((a) => fxTruthy(evalNode(a))) ? 1 : 0;
      case 'NOT': return fxTruthy(evalNode(args[0])) ? 0 : 1;
      case 'CONCAT': return argAll(args).map((x) => (typeof x === 'number' ? fxFormat(x) : (x ?? '').toString())).join('');
      case 'MEDIAN': { const n = argNums(args).slice().sort((a, b) => a - b); if (!n.length) throw { code: '#ЧИСЛО' }; const m = Math.floor(n.length / 2); return n.length % 2 ? n[m] : (n[m - 1] + n[m]) / 2; }
      case 'COUNTIF': { const v = evalNode(args[0]); const vals: any[] = v && (v as any).__range ? (v as any).__range : [v]; const crit = (evalNode(args[1]) ?? '').toString(); return vals.filter((x) => matchCrit(x, crit)).length; }
    }
    throw { code: '#ИМЯ?' };
  }

  function evalNode(n: any): any {
    switch (n.t) {
      case 'num': return n.v;
      case 'str': return n.v;
      case 'ref': { const pr = parseRef(n.ref); if (!pr) throw { code: '#ИМЯ?' }; return resolveCell(pr.c, pr.r); }
      case 'range': return { __range: rangeVals(n.from, n.to) };
      case 'unary': { const v = fxNum(evalNode(n.arg)); return n.op === '-' ? -v : v; }
      case 'bin': return evalBin(n);
      case 'call': return evalCall(n.name, n.args);
    }
    throw { code: '#ОШ' };
  }

  return function display(rowId: string, colId: string): { text: string; error: boolean } {
    const c = colIdx.get(colId); const r = rowIdx.get(rowId);
    if (c == null || r == null) return { text: '', error: false };
    try {
      const v = resolveCell(c, r);
      return { text: fxFormat(v), error: false };
    } catch (e: any) {
      return { text: (e && e.code) || '#ОШ', error: true };
    }
  };
}
/* eslint-enable @typescript-eslint/no-explicit-any */
